// Phase 25.16c — Outreach templates for cold email and warm follow-ups.
// Edit freely — these are surfaced as copy-paste cards in /app/admin/system.
// Each template ships subject + body with [BRACKETED] placeholders to
// customise per recipient. The UI ships a "Copy email" button that copies
// the whole thing minus the subject (since most email clients want subject
// pasted separately) and a "Copy subject only" button.

export const OUTREACH_TEMPLATES = [
  // ── PROSPECT — INDUSTRIAL / MINING ───────────────────────────
  {
    slug: "cold-bellevue-gold",
    category: "Industrial · Mining",
    title: "Bellevue Gold — Mine Superintendent / GM Operations",
    use_case: "WA tier-3 single-asset gold producer · Goldfields region · OPC UA / Aveva PI estate likely",
    research_hint: "LinkedIn search: \"Bellevue Gold\" AND (\"Mine Superintendent\" OR \"GM Operations\" OR \"Operations Manager\")",
    subject: "Bellevue Gold · 25 minutes · WA gold ops intelligence (research, not pitch)",
    body: `Hi [first name],

Emmanuel here — Perth-based founder of DARA OS. Building a sovereign Australian industrial intelligence layer that reads historian/SCADA data (Aveva PI, OPC UA, Modbus) and writes one-page daily briefs for mining operations teams. Audit-tagged against IEC 62443 and AS/NZS 4360.

I noticed Bellevue's just hit steady-state production at the Bellevue mine — congrats on what's clearly a clean ramp. Single-asset gold producer, Goldfields region, hungry-team setup. Honestly, this is exactly the operator profile I'm trying to understand better before I build out the next iteration.

I'm NOT pitching anything yet. Looking for 25 minutes of your time on a research call — I want to understand where the real reliability/throughput pain sits at a tier-3 WA gold operator, and whether what I've built is actually relevant. In exchange I'll share what I'm hearing across 3-4 WA peers, and you can see the live demo if it's interesting.

→ daraos.ai/industrial-demo · hit "Inject anomaly" on the mining profile

Worth a 25?

Emmanuel
[mobile]
daraos.ai`,
  },
  {
    slug: "cold-capricorn-metals",
    category: "Industrial · Mining",
    title: "Capricorn Metals — Mine Manager / Process Engineer",
    use_case: "WA tier-3 gold producer · Karlawinda single asset · cash-flowing · lean ops team",
    research_hint: "LinkedIn search: \"Capricorn Metals\" AND (\"Mine Manager\" OR \"Process Engineer\" OR \"Continuous Improvement\") · target Karlawinda-based names",
    subject: "Karlawinda + DARA · 25-min research call",
    body: `Hi [first name],

Emmanuel, Perth founder of DARA OS. I'm building sovereign Australian industrial AI — read-only intelligence layer on top of Aveva PI / OPC UA / Modbus, generating one-page daily ops briefs for mid-tier mining + utility operators. AS/NZS 4360 and IEC 62443 aligned.

Karlawinda's a perfect profile for what I'm trying to understand — single producing asset, lean team, mature processing flowsheet, and the kind of operation where one missed anomaly costs you a day's gold. I'm not selling you anything yet; I want to ask the right questions of someone who actually runs ops at this scale.

25 minutes, research call, no deck unless you ask. In return I'll share what I'm hearing across 4 WA gold peers. The demo is on daraos.ai/industrial-demo if you want a look first.

Worth a chat?

Emmanuel
[mobile]
daraos.ai`,
  },

  // ── PROSPECT — WATER UTILITY ─────────────────────────────────
  {
    slug: "cold-busselton-water",
    category: "Industrial · Water Utility",
    title: "Busselton Water — CEO (direct)",
    use_case: "WA regional government-owned water utility · ~50 staff · small enough to email CEO directly · regulator-pack alignment matters here",
    research_hint: "busseltonwater.wa.gov.au → Board / Leadership page for current CEO name. VERIFY 'Wonnerup borefield' is still active before sending (or remove that specific reference).",
    subject: "Busselton Water · brief from a WA founder",
    body: `Hi [CEO first name],

Emmanuel Maji, Perth-based founder of DARA OS. I'm building an Australian sovereign industrial intelligence layer — read-only AI on top of SCADA/historian data, generating reconciled daily operations briefs for water utilities. Aligned to AS/NZS 4360, IEC 62443, and the WSAA G2 cyber framework.

Busselton Water is exactly the size of utility I'd love to learn from. Big enough to have real ops complexity (the Wonnerup borefield + 12 reservoirs is hardly trivial), small enough that you'd see the value of a tool that gives you a single morning brief instead of 4 separate dashboards. And being a WA utility, the regulator-pack alignment matters in a way it wouldn't for a private operator.

I'm asking you for 30 minutes — not to pitch, but to understand. I want to learn where your operations team's real pain sits between data and decisions, and whether what I've built is actually useful. In exchange I'll share what I'm seeing across the AU industrial space, and show you the live demo + the regulator-pack preview we generate.

→ daraos.ai/industrial-demo · hit "Inject anomaly" on the water utility profile
→ daraos.ai/regulator-pack-preview · the kind of artefact your auditor and Department of Water would want

If Busselton ran a 12-week paid pilot with DARA at any point, the WSAA G2 / IEC 62443 alignment work we'd do together would also give you something concrete to take to your board. Worth a chat?

Emmanuel
[mobile]
emmanuel@daraos.ai
daraos.ai`,
  },

  // ── ANGEL INVESTOR — WA ──────────────────────────────────────
  {
    slug: "angel-wa",
    category: "Investor · WA Angels",
    title: "WA Angel investor cold message (Perth Angels / WA AngelLink / ex-mining + utility execs)",
    use_case: "WA-based angels who get Australian sovereign positioning · attach SAFE pack ONLY after reply",
    research_hint: "Perth Angels member directory · WA AngelLink · LinkedIn 'angel investor' + 'Perth' filter · add 1 personalised top sentence about their background",
    subject: "Perth founder · industrial AI · 90 seconds",
    body: `Hi [First name],

I'm Emmanuel, founder of DARA OS — building an Australian sovereign industrial intelligence layer out of Perth. Read-only AI that turns historian, POS, and accounting data into one-page daily briefs for tier-2 mining operators, water utilities, and SMBs. Live product, growing customer base.

90 seconds:
• Two verticals, one engine. Mining + utility tier-2s (where the regulator pack actually matters) and SMBs (POS Live in 50+ shops within 6 months). One platform, two GTM motions.
• Moat is the boring stuff. IEC 62443 / NIST CSF / AS/NZS 4360 alignment baked in. Sovereign AU data. ACSC ISM control 1735 compliant. No Big Four AI vendor can ship this credibility in AU in 18 months.
• Raising A$1.25M on a A$7M post-money SAFE cap. 20% discount. MFN. Founder retains 53-58% post-Seed. Closing first cheques August.
• What I want from you: a 20-minute Zoom this week. Not pitching for a cheque — testing whether the moat thesis lands with someone who's seen 50+ pre-seed AU SaaS pitches.

See it live: daraos.ai/industrial-demo (water utility profile — hit "Inject anomaly" and watch DARA write the brief)

Worth 20 mins?

Emmanuel
[mobile]
daraos.ai`,
  },

  // ── ANGEL INVESTOR — NON-WA ──────────────────────────────────
  {
    slug: "angel-non-wa-aud",
    category: "Investor · AU Angels (Sydney / Melbourne / Brisbane)",
    title: "Non-WA Australian Angel cold message · AUD SAFE",
    use_case: "AU SaaS angels outside WA · attach AUD SAFE pack only after reply · 1-sentence personalisation at top",
    research_hint: "Search LinkedIn 'angel investor' + 'Sydney/Melbourne/Brisbane' · cross-reference Cut Through Venture or Startmate alumni · pick 5-7 names per batch",
    subject: "AU sovereign industrial AI · 90-sec read",
    body: `Hi [First name],

Emmanuel here — founder of DARA OS, an Australian sovereign industrial intelligence layer. Read-only AI on top of historian, POS, and accounting data, writing one-page daily briefs for tier-2 mining operators, water utilities, and SMBs. Live product. Perth-built.

The shape of it:
• Two GTM motions, one engine. Industrial (high ACV, A$60-180k tier-2 mining / utility pilots) + SMB (volume, A$50-499/mo). Same platform; the industrial side underwrites the SMB credibility flywheel.
• Defensibility ≠ the AI. It's IEC 62443-3-3 + NIST CSF 2.0 + AS/NZS 4360 alignment baked into the product. Sovereign AU hosting. ACSC ISM compliant. Things horizontal AI vendors can't ship in AU in 18 months. This is the moat, not the LLM.
• Pre-seed: raising A$1.25M on a A$7M post-money SAFE cap. 20% discount. MFN. Pro-rata for ≥A$100k. Founder retains 53-58% across all Series Seed scenarios I've modelled. Closing through August.
• Asking you for: 20 minutes this week. Not pitching the cheque yet — I want to test whether the moat thesis holds up under your scrutiny first.

Two things to see before the call:
→ daraos.ai/industrial-demo · hit "Inject anomaly" on the water utility profile
→ daraos.ai/regulator-pack-preview · screenshot of what we ship at the end of pilot week 4

Worth a 20?

Emmanuel
[mobile]
daraos.ai`,
  },

  // ── ANGEL INVESTOR — US ──────────────────────────────────────
  {
    slug: "angel-us-usd",
    category: "Investor · US / SV Angels",
    title: "US / Silicon Valley Angel cold message · USD SAFE",
    use_case: "US accredited investors · attach USD SAFE pack only after reply · be ready to discuss Delaware flip path",
    research_hint: "Target ex-Anthropic, ex-Snowflake, ex-Palantir, ex-Honeywell, ex-Schneider Electric on LinkedIn. Industrial AI / vertical SaaS angels. AngelList Syndicate leads also good.",
    subject: "AU sovereign industrial AI · 90-sec read",
    body: `Hi [First name],

Emmanuel here — founder of DARA OS, an Australian sovereign industrial intelligence layer. Read-only AI reading historian / SCADA data and writing daily ops briefs for tier-2 industrial operators and SMBs. Live product. Perth-incorporated; Delaware flip on the table at Series A.

Why an Australian AI startup matters to you:
• The regulator pack — IEC 62443-3-3, NIST CSF 2.0, AS/NZS 4360, ACSC ISM — is genuinely hard to ship in 18 months. Horizontal US AI vendors won't replicate this in AU before we lock the market. Sovereign AU positioning is a 5-year moat in regulated verticals.
• Two GTM motions: industrial tier-2 mining + utilities (US$45-130k ACV pilots) and SMB POS live (volume play). The industrial side is what underwrites the equity story.
• Pre-seed: raising US$1M on a US$7M post-money SAFE cap. 20% discount. MFN. Pro-rata for ≥US$75k. Reg D 506(b) / Reg S compliant. Founder retains 56-61% across Series Seed scenarios.

Two demo links before the call:
→ daraos.ai/industrial-demo · live SCADA simulator, water utility profile
→ daraos.ai/regulator-pack-preview · the artefact pack we ship at pilot week 4

Asking for 20 minutes. Not pitching the cheque yet — testing the moat thesis with someone who's seen industrial AI from inside.

Worth a chat?

Emmanuel
[mobile / WhatsApp]
emmanuel@daraos.ai
daraos.ai`,
  },

  // ── SOCIAL · LINKEDIN ────────────────────────────────────────
  {
    slug: "linkedin-post-1-customer-research",
    category: "Social · LinkedIn",
    title: "LinkedIn post · Customer-research opener (Tuesday 7-9am AWST)",
    use_case: "First-in-series post that signals you spent time talking to operators before building. Drops Bezos comparison. No hashtag bloat. Optimised for ops manager engagement.",
    research_hint: "Best posting times: Tue/Wed/Thu 7-9am AWST. Reply within 30 min to early comments — the algorithm rewards it heavily.",
    subject: "(no subject — LinkedIn post)",
    body: `Spent 10 weeks talking to WA mining superintendents before writing one line of code.

Three things kept coming up:

1. They check 4 separate dashboards every morning to answer one question.
2. They don't trust AI vendors who can't show them their own IEC 62443 alignment.
3. The Big Four AI companies will never ship a regulator-pack for AU water.

DARA OS is the layer that fixes all three. Read-only intelligence on top of your existing historian, daily one-page brief at 06:00 local, audit-tagged against IEC 62443 + AS/NZS 4360.

If you run mining or utility ops in WA and want to see what your morning brief would actually look like — DM me. 25-minute research call, no deck, no pitch.

daraos.ai/industrial-demo

#MiningWA #IndustrialAI #WaterUtilities`,
  },
  {
    slug: "linkedin-post-2-screenshot-proof",
    category: "Social · LinkedIn",
    title: "LinkedIn post · Technical proof (Wed/Thu next week)",
    use_case: "Concrete demonstration post. Pair with a screenshot of DARA flagging a real anomaly. Builds the 'this product works' impression that drives DMs.",
    research_hint: "Attach a screenshot of /industrial-demo with a cavitation or chlorine-dose anomaly firing + brief panel populated. CROP TIGHT around the brief — full screenshots dilute the visual impact.",
    subject: "(no subject — LinkedIn post)",
    body: `60 seconds after a fictional water-utility booster started showing axial vibration + discharge-pressure instability, DARA wrote this:

"Cavitation signature: axial vibration rising with discharge-pressure instability. Likely cause: net positive suction head margin lost — air ingress or wet-well draw-down. Action: inspect suction strainer and wet-well makeup, target completion before next pumping cycle."

That's not a chart. That's not a notification.

That's a sentence an operator can act on at 6:05am, before the morning meeting.

Every anomaly tagged to IEC 62443 + AS/NZS 4360, so when your CISO asks "what's the audit trail" you don't need to scramble.

This is what DARA does. Live demo, no signup, no friction: daraos.ai/industrial-demo

#IndustrialAI #WaterUtilities #SCADA`,
  },
  {
    slug: "linkedin-post-3-founder-reflection",
    category: "Social · LinkedIn",
    title: "LinkedIn post · Founder reflection (week 2, Tue/Wed)",
    use_case: "Personal-but-not-cringe post. Builds founder credibility. Shows you're thinking, not just shipping. Best for attracting angels who lurk for 6 weeks before reaching out.",
    research_hint: "Post no more than once per fortnight. Reflections every week look performative. Honest is better than polished — share one real surprise from the week.",
    subject: "(no subject — LinkedIn post)",
    body: `One thing I got wrong this week as a founder:

I built DARA's pricing tiers from $50 (micro) to $5,000 (industrial). I thought the micro tier was the easy sell — barber shops, food trucks, lower stakes.

It's not. Convincing a barber that an AI brief is worth $50/month is harder than convincing a tier-2 miner that one is worth $5,000.

Why? The barber needs to believe AI applies to him. The miner already does. He's just deciding which vendor.

Lesson: trust isn't proportional to cheque size. It's inversely proportional to how abstract your value sounds.

Building DARA out of Perth. Sovereign Australian industrial intelligence layer. Daily ops briefs from your existing data. daraos.ai

#FounderJourney #B2BSaaS`,
  },

  // ── FOLLOW-UP / BUMP ──────────────────────────────────────────
  {
    slug: "follow-up-bump",
    category: "Follow-up · Day 4 after any cold email",
    title: "Day-4 bump (no reply received)",
    use_case: "Send 4 days after any cold email above if no reply · ~25-30% of non-responders reply to this · use sparingly (one bump only)",
    research_hint: "Send Monday or Tuesday morning local time. Don't bump twice — if no reply after the bump, move on.",
    subject: "Re: [original subject]",
    body: `[First name] — quick bump.

If "no" or "not now", a one-line "pass" gets you off my list with no offence taken.

If "maybe" — what's the single hardest question you'd want answered before a 20-min call? I'll send the answer in writing first so we don't waste your time.

Emmanuel`,
  },
];
